/* eslint-disable import/no-cycle */

import { useState, useEffect, useRef } from 'react'
import Spinner from '@/icons/bwi/spinner.svg'
import { isPrint } from '@/utils/print'
import useMutationObserver from '@/utils/useMutationObserver'
import { TypeComponentHubiloForm } from '@/types/ctf'
import './hubilo-form.css'

type HubiloFormProps = TypeComponentHubiloForm<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  id: string
}

export default function HubiloForm({ id, formId, plausibleEventName }: HubiloFormProps): JSX.Element {
  const [loaded, setLoaded] = useState(false)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const targetId = `hbl-${id}`

  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://cdn.hubilo.com/ticketing/widget/static/js/ticketing-widget.js'

    script.async = true
    document.body.appendChild(script)
    script.onload = () => setLoaded(true)
  }, [])

  useEffect(
    () => () => {
      // remove the style tag we had to apply when component unmounts
      if (!document.getElementById(targetId)) {
        document.getElementsByTagName('html')[0].removeAttribute('style')
      }
    },
    []
  )

  useEffect(() => {
    loaded &&
      window['@hubilotechnologies/ticketing-micro-fe'].init({
        container: document.getElementById(targetId),
        props: {
          communityUrl: `https://events.hubilo.com/${formId}`,
          languageCode: 'EN',
          showEventInfo: false,
          hideEventLogo: false,
          hideEventSocialLinks: false,
          isWidgetExpand: true,
          environment: 'production',
        },
      })
    document.getElementsByTagName('html')[0].setAttribute('style', 'font-size: 16px !important')
    setScriptLoaded(true)
  }, [loaded])

  // Add the plausible event name to the buttons once they are rendered
  const ref = useRef<HTMLDivElement>(null)
  useMutationObserver(
    ref,
    (mutationList, observer) => {
      if (typeof plausibleEventName !== 'string') {
        observer.disconnect()
        return
      }
      const buttons = ref.current?.querySelectorAll('button')
      buttons?.forEach((button) => {
        if (!button.classList.contains('moreInfo') && !button.classList.contains(plausibleEventName)) {
          button.classList.add(
            ...plausibleEventName
              .replace(/\s+/g, ' ')
              .trim()
              .split(' ')
          )
          observer.disconnect()
        }
      })
    },
    {
      childList: true,
      subtree: true,
    }
  )

  if (isPrint()) return <></>
  return (
    <div className="min-h-[10rem]">
      {!loaded && (
        <div className="w-full flex justify-center p-8">
          <Spinner className="animate-spin h-16 w-16 text-secondary-300" />
        </div>
      )}

      <div id={targetId} ref={ref} />
    </div>
  )
}
